<template>
  <avs-card>
    <template v-slot:title>
      <v-card-title class="pl-0">{{ $t("Site") }}</v-card-title>
    </template>
    <template v-slot:content>
      <v-container>
        <v-form :readonly="!isEditable" ref="form">
          <!-- Nom + Adresse -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="shopLocation.name"
                :disabled="!isEditable"
                :rules="rules.name"
                :label="$t('Nom')"
                counter="64"
                maxlength="64"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="shopLocation.address"
                :disabled="!isEditable"
                :rules="rules.address"
                :label="$t('Adresse')"
                counter="64"
                maxlength="64"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <!-- Ville + Commune -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                v-model="shopLocation.city"
                clearable
                :items="cities"
                :disabled="!isEditable"
                dense
                outlined
                :rules="rules.city"
                item-text="name"
                item-value="id"
                :label="$t('Ville')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="shopLocation.municipality"
                clearable
                :items="municipalities"
                :disabled="!isEditable"
                dense
                outlined
                :rules="rules.municipality"
                item-text="name"
                item-value="id"
                :label="$t('Commune')"
              />
            </v-col>
          </v-row>

          <!-- Longitude + Latitude -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="shopLocation.longitude"
                :disabled="!isEditable"
                :rules="rules.longitude"
                :label="$t('Longitude')"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="shopLocation.latitude"
                :disabled="!isEditable"
                :rules="rules.latitude"
                :label="$t('Latitude')"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <!-- Numero Avalanche (pleine largeur) -->
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="shopLocation.shop_avalanche_id"
                :disabled="!isEditable"
                :rules="rules.shopAvalancheId"
                :label="$t('Numéro Avalanche')"
                counter="6"
                maxlength="6"
                outlined
                dense
                prepend-inner-icon="mdi-identifier"
                :hint="$t('Identifiant Avalanche de la station (max 6 caractères)')"
                persistent-hint
              />
            </v-col>
          </v-row>

          <!-- Restriction de visibilite -->
          <v-row dense>
            <v-col cols="12">
              <v-switch
                v-model="shopLocation.restrict_visibility"
                :disabled="!isEditable"
                :label="$t('Restreindre la visibilité de cette station')"
                inset
                color="primary"
                hide-details
                @change="onRestrictToggle"
              />
            </v-col>
          </v-row>

          <!-- Societes autorisees -->
          <v-row v-if="shopLocation.restrict_visibility" dense>
            <v-col cols="12">
              <v-autocomplete
                v-model="shopLocation.allowed_recipient_companies"
                :disabled="!isEditable"
                :items="companies"
                item-text="name"
                item-value="pk"
                :label="$t('Sociétés autorisées')"
                multiple
                chips
                small-chips
                deletable-chips
                outlined
                dense
                :loading="loadingCompanies"
                :hint="$t('Sélectionnez les sociétés qui peuvent voir et utiliser cette station')"
                persistent-hint
              />
            </v-col>
          </v-row>
          <!-- <div style="margin-bottom: 25px">
            <label>{{ $t("commons.description") }}</label>
            <ckeditor
              :editor="editor"
              :disabled="!isEditable"
              tag-name="textarea"
              rows="5"
              v-model="shopLocation.description"
              :config="editorConfig"
              :hint="$t('views.template.hint.description')"
              :label="$t('commons.message')"
              className="content-element"
            ></ckeditor>
          </div> -->
          <v-col class="d-flex justify-end gap-4">
            <avs-saveButton v-if="!loading" @click="save" :disabled="!isEditable" />
            <avs-button
              v-else
              icon
              color="secondary"
              class="spin-loading"
              :iconI="loading ? 'mdi-loading spin' : ''"
              label
              fab
              small
            />
            <avs-cancelButton @click="cancel" />
          </v-col>
        </v-form>
      </v-container>
    </template>
  </avs-card>
</template>

<script>
import { mkShop } from "@/utils/datamodels";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { ShopDetailService, StaffMembersService, CityDetailService, MunicipalityDetailService, CompanyService } from "@/services";
import { required, max64, isDecimal } from "@/utils/validator";
import _ from "lodash";

export default {
  name: "ShopEditor",
  props: {
    hasUniqueName: {
      type: Function,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Object,
    },
  },
  data() {
    return {
      defaultItems: [],
      shop: mkShop(),
      loading: false,
      // shopLocation est maintenant en data (et non en computed) pour
      // garantir la reactivite Vue 2 sur les nouveaux champs (restrict_visibility,
      // allowed_recipient_companies, shop_avalanche_id) qui peuvent ne pas
      // exister sur un shop ancien recu de l'API.
      shopLocation: this.buildInitialShopLocation(),
      rules: {
        description: [],
        name: [required, max64],
        address: [required, max64],
        latitude : [isDecimal],
        longitude : [isDecimal],
        city : [required],
        municipality : [required],
        shopAvalancheId: [
          v => !v || v.length <= 6 || this.$t("Maximum 6 caractères"),
        ],
      },
      users: [],
      cities: [],
      municipalities: [],
      companies: [],
      loadingCompanies: false,
      // editor: ClassicEditor,
      // editorConfig: {
      //   toolbar: [
      //     "paragraph",
      //     "heading1",
      //     "heading2",
      //     "bold",
      //     "italic",
      //     "|",
      //     "link",
      //     "|",
      //     "undo",
      //     "redo",
      //     "-",
      //     "numberedList",
      //     "bulletedList",
      //   ],
      // },
    };
  },
  watch: {
    // Si le parent change la valeur (chargement async d'un shop existant),
    // on rebuild l'objet local pour qu'il soit reactif sur tous les champs,
    // y compris les nouveaux qui n'etaient pas dans la reponse API.
    value: {
      immediate: false,
      deep: false,
      handler() {
        this.shopLocation = this.buildInitialShopLocation()
      },
    },
  },
  created() {
    this.fetchCityTypes();
    this.fetchMunicipalityTypes();
    this.fetchCompanies();
  },
  methods: {
    buildInitialShopLocation() {
      const defaults = mkShop()
      const incoming = _.cloneDeep(this.value || {})
      // Merge : les valeurs API ecrasent les defaults, mais les defaults
      // garantissent l'existence des cles necessaires a la reactivite Vue.
      const merged = { ...defaults, ...incoming }
      // Cas particulier : la M2M peut arriver sous forme de detail enrichi
      // (allowed_companies_detail) ou de simple liste de PK. On normalise vers PK.
      if (Array.isArray(merged.allowed_recipient_companies)) {
        merged.allowed_recipient_companies = merged.allowed_recipient_companies.map(
          c => (c && typeof c === "object" ? (c.pk || c.id) : c)
        )
      } else if (Array.isArray(incoming.allowed_companies_detail)) {
        merged.allowed_recipient_companies = incoming.allowed_companies_detail.map(c => c.pk)
      } else {
        merged.allowed_recipient_companies = []
      }
      return merged
    },

    onRestrictToggle(val) {
      // Si on desactive la restriction, on vide la liste des societes autorisees
      if (!val) {
        this.shopLocation.allowed_recipient_companies = []
      }
    },

    fetchCityTypes() {
      CityDetailService.getAll(this).then(response => {
        this.cities = response
      })
    },
    fetchMunicipalityTypes() {
      MunicipalityDetailService.getAll(this).then(response => {
        this.municipalities = response
      })
    },
    fetchCompanies() {
      this.loadingCompanies = true
      CompanyService.getAllCompanies()
        .then(r => {
          this.companies = (r || []).filter(c => c.company_type === "Business")
        })
        .catch(() => { this.companies = [] })
        .finally(() => { this.loadingCompanies = false })
    },
    fetchFieldTypes() {
      StaffMembersService.get(this).then((response) => {
        this.users = response.results;
      });
    },
    getDefaultValue() {
      return mkShop();
    },
    cancel() {
      this.$emit("cancel");
    },
    async performSave(data) {
      if (data.id > 0) {
        return ShopDetailService.put(data);
      } else {
        return ShopDetailService.post(data);
      }
    },
    async save() {
      this.loading = true;

      if (this.$refs.form.validate()) {
        try {
          const response = await this.performSave(this.shopLocation);
          this.$emit("input", response);
          this.$emit("success", this.$t("Le site a été sauvegardé avec succès."));
        } catch (error) {
          const message = error?.response?.data?.detail || this.$t("Une erreur est survenue.");
          this.$emit("error", message);
        } finally {
          this.loading = false;
        }
      } else {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.ck.ck-editor__main > .ck-editor__editable {
  height: 200px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}
.ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
.ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
.ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.ck-editor__editable {
  color: rgba(0, 0, 0, 0.87) !important;
}

.spin-loading {
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
